import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
  Switch,
  Divider,
  Alert
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  PlayCircleOutlined,
  BookOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import quizAPI from '../../services/quizAPI';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  getClassSchoolId,
  getPrimarySchoolId,
  resolveDefaultSchoolId,
  resolveOperationSchoolId
} from '../../utils/schoolScope';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/**
 * 无线答题题库管理页面
 */
const QuizManager = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [form] = Form.useForm();

  // 筛选条件
  const [filters, setFilters] = useState({
    classId: null,
    schoolId: getPrimarySchoolId(user),
    includeHidden: false
  });

  useEffect(() => {
    loadSchools();
    loadClasses();
    loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // 加载学校列表
  const loadSchools = async () => {
    try {
      const response = await api.get('/schools');
      if (response.data.success) {
        const availableSchools = response.data.data || [];
        setSchools(availableSchools);
        const defaultSchoolId = resolveDefaultSchoolId(user, availableSchools);
        if (!filters.schoolId && defaultSchoolId) {
          setFilters((current) => ({ ...current, schoolId: defaultSchoolId }));
        }
      }
    } catch (error) {
      console.error('加载学校列表失败:', error);
    }
  };

  // 加载班级列表
  const loadClasses = async () => {
    try {
      const response = await api.get('/classes');
      if (response.data.success) {
        setClasses(response.data.data || []);
      }
    } catch (error) {
      console.error('加载班级列表失败:', error);
    }
  };

  // 加载题目列表
  const loadQuestions = async () => {
    try {
      setLoading(true);
      const response = await quizAPI.getQuestions(filters);
      if (response.data.success) {
        setQuestions(response.data.data || []);
      } else {
        message.error('加载题目列表失败');
      }
    } catch (error) {
      console.error('加载题目列表失败:', error);
      message.error('加载题目列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 打开新建/编辑对话框
  const handleOpenModal = (question = null) => {
    setEditingQuestion(question);
    setModalVisible(true);

    if (question) {
      // 编辑模式
      form.setFieldsValue({
        title: question.title,
        type: question.type,
        correctAnswer: question.correct_answer,
        classId: question.class_id,
        schoolId: question.school_id,
        options: question.options
      });
    } else {
      // 新建模式 - 默认单选题4个选项
      form.setFieldsValue({
        type: 'single',
        options: [
          { key: 'A', value: '1', label: '' },
          { key: 'B', value: '2', label: '' },
          { key: 'C', value: '3', label: '' },
          { key: 'D', value: '4', label: '' }
        ],
        classId: filters.classId,
        schoolId: filters.schoolId
      });
    }
  };

  // 关闭对话框
  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingQuestion(null);
    form.resetFields();
  };

  // 保存题目
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // 验证选项
      const hasEmptyLabel = values.options.some(opt => !opt.label || opt.label.trim() === '');
      if (hasEmptyLabel) {
        message.error('请填写所有选项内容');
        return;
      }

      const data = {
        title: values.title,
        type: values.type,
        options: values.options,
        correctAnswer: values.correctAnswer || null,
        classId: values.classId || null,
        schoolId: resolveOperationSchoolId({
          classes,
          classId: values.classId,
          item: editingQuestion,
          selectedSchoolId: values.schoolId || filters.schoolId,
          user
        })
      };

      if (editingQuestion) {
        // 更新
        await quizAPI.updateQuestion(editingQuestion.id, data);
        message.success('更新成功');
      } else {
        // 创建
        await quizAPI.createQuestion(data);
        message.success('创建成功');
      }

      handleCloseModal();
      loadQuestions();
    } catch (error) {
      console.error('保存失败:', error);
      if (error.errorFields) {
        // 表单验证失败
        return;
      }
      message.error('保存失败');
    }
  };

  // 删除题目
  const handleDelete = async (id) => {
    try {
      await quizAPI.deleteQuestion(id);
      message.success('删除成功');
      loadQuestions();
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  // 切换显示/隐藏
  const handleToggleHidden = async (id) => {
    try {
      await quizAPI.toggleQuestion(id);
      message.success('操作成功');
      loadQuestions();
    } catch (error) {
      console.error('操作失败:', error);
      message.error('操作失败');
    }
  };

  // 开始答题
  const handleStartQuiz = (question) => {
    navigate('/quiz-control', { state: { question } });
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60
    },
    {
      title: '题目',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (text, record) => (
        <div>
          <Text>{text}</Text>
          {record.correct_answer && (
            <Tag color="green" style={{ marginLeft: 8 }}>答案: {record.correct_answer}</Tag>
          )}
        </div>
      )
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => (
        <Tag color={type === 'single' ? 'blue' : 'orange'}>
          {type === 'single' ? '单选题' : '评分题'}
        </Tag>
      )
    },
    {
      title: '选项数',
      key: 'optionCount',
      width: 80,
      render: (_, record) => record.options?.length || 0
    },
    {
      title: '归属',
      key: 'owner',
      width: 150,
      render: (_, record) => {
        if (record.class_name) {
          return <Tag color="purple">{record.class_name}</Tag>;
        } else if (record.school_name) {
          return <Tag color="cyan">{record.school_name}</Tag>;
        } else {
          return <Tag>系统预设</Tag>;
        }
      }
    },
    {
      title: '状态',
      dataIndex: 'hidden',
      key: 'hidden',
      width: 80,
      render: (hidden) => (
        <Tag color={hidden ? 'default' : 'success'}>
          {hidden ? '已隐藏' : '显示中'}
        </Tag>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (text) => text ? new Date(text).toLocaleString('zh-CN') : '-'
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 240,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() => handleStartQuiz(record)}
          >
            开始答题
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          >
            编辑
          </Button>
          <Button
            size="small"
            icon={record.hidden ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            onClick={() => handleToggleHidden(record.id)}
          >
            {record.hidden ? '显示' : '隐藏'}
          </Button>
          <Popconfirm
            title="确定删除这个题目吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3}>
              <BookOutlined /> 无线答题题库管理
            </Title>
            <Paragraph type="secondary">
              管理无线答题题目，支持单选题和评分题。题目可用于ESP32红外遥控器答题。
            </Paragraph>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => handleOpenModal()}
            >
              新建题目
            </Button>
          </Col>
        </Row>

        <Alert
          message="使用提示"
          description="系统预设题目可直接使用。您也可以创建班级专属题目或全校通用题目。"
          type="info"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Select
              placeholder="筛选学校"
              allowClear
              style={{ width: '100%' }}
              value={filters.schoolId}
              onChange={(value) => setFilters({ ...filters, schoolId: value })}
            >
              {schools.map(school => (
                <Option key={school.id} value={school.id}>{school.name}</Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="筛选班级"
              allowClear
              style={{ width: '100%' }}
              value={filters.classId}
              onChange={(value) => setFilters({
                ...filters,
                classId: value,
                schoolId: getClassSchoolId(classes, value) || filters.schoolId
              })}
            >
              {classes.map(cls => (
                <Option key={cls.id} value={cls.id}>{cls.name}</Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <Space>
              <Text>显示隐藏题目:</Text>
              <Switch
                checked={filters.includeHidden}
                onChange={(checked) => setFilters({ ...filters, includeHidden: checked })}
              />
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={questions}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 个题目`
          }}
        />
      </Card>

      {/* 新建/编辑对话框 */}
      <Modal
        title={editingQuestion ? '编辑题目' : '新建题目'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={handleCloseModal}
        width={800}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            label="题目标题"
            name="title"
            rules={[{ required: true, message: '请输入题目标题' }]}
          >
            <TextArea rows={3} placeholder="请输入题目标题" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="题型"
                name="type"
                rules={[{ required: true }]}
              >
                <Select onChange={(value) => {
                  // 切换题型时重置选项
                  if (value === 'score') {
                    form.setFieldsValue({
                      options: [
                        { key: '0', value: '0', label: '0分' },
                        { key: '1', value: '1', label: '1分' },
                        { key: '2', value: '2', label: '2分' },
                        { key: '3', value: '3', label: '3分' },
                        { key: '4', value: '4', label: '4分' },
                        { key: '5', value: '5', label: '5分' },
                        { key: '6', value: '6', label: '6分' },
                        { key: '7', value: '7', label: '7分' },
                        { key: '8', value: '8', label: '8分' },
                        { key: '9', value: '9', label: '9分' }
                      ]
                    });
                  }
                }}>
                  <Option value="single">单选题</Option>
                  <Option value="score">评分题(0-9分)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="所属班级"
                name="classId"
              >
                <Select placeholder="留空表示全校通用" allowClear>
                  {classes.map(cls => (
                    <Option key={cls.id} value={cls.id}>{cls.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="正确答案"
                name="correctAnswer"
              >
                <Input placeholder="可选，问卷调查无需填写" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>选项设置</Divider>

          <Form.List name="options">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <Row key={field.key} gutter={8} align="middle">
                    <Col span={4}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'key']}
                        noStyle
                      >
                        <Input disabled />
                      </Form.Item>
                    </Col>
                    <Col span={16}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'label']}
                        rules={[{ required: true, message: '请输入选项内容' }]}
                      >
                        <Input placeholder="选项内容" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      {form.getFieldValue('type') === 'single' && fields.length > 2 && (
                        <Button
                          type="link"
                          danger
                          onClick={() => remove(field.name)}
                        >
                          删除
                        </Button>
                      )}
                    </Col>
                  </Row>
                ))}
                {form.getFieldValue('type') === 'single' && fields.length < 9 && (
                  <Button
                    type="dashed"
                    onClick={() => {
                      const nextKey = String.fromCharCode(65 + fields.length); // A-I
                      add({ key: nextKey, value: String(fields.length + 1), label: '' });
                    }}
                    block
                  >
                    添加选项
                  </Button>
                )}
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default QuizManager;
